// Three independent Properties sections: EFFECT, FILTER, SHADOW. Each has its
// own catalog and at most one active preset, stored on its own element field:
//   element.effect       = { type, params }
//   element.filterPreset = { type, params }
//   element.shadow       = { type, params }
// All compose into one CSS filter chain (+ a box-shadow for inner shadow).
//
// Migration: old designs used a single `element.effect` that mixed filters and
// shadow, plus a raw `element.filter` string and legacy `element.shadowPreset`.
// getEffectFields() normalizes any of those into the three-field model at read
// time (non-destructive — it never mutates the element).

const px = (n) => `${Number(n) || 0}px`;
const num = (n, d = 0) => (Number.isFinite(Number(n)) ? Number(n) : d);

function hexToRgba(hex, opacity = 1) {
  const m = String(hex || '').match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return hex || `rgba(0,0,0,${opacity})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${opacity})`;
}

// ── Catalogs ────────────────────────────────────────────────────────────────
// scope: 'visual' | 'text' | 'both'. controls: param keys the UI should expose.
export const EFFECT_CATALOG = [
  { id: 'none', label: 'None', scope: 'both' },
  { id: 'glow', label: 'Glow', scope: 'both', defaults: { color: '#ffffff', blur: 12, opacity: 0.9, size: 1 }, controls: ['color', 'blur', 'opacity', 'size'] },
  { id: 'neon', label: 'Neon', scope: 'both', defaults: { color: '#22d3ee', blur: 16, intensity: 1.4, opacity: 1 }, controls: ['color', 'blur', 'intensity', 'opacity'] },
  { id: 'outline', label: 'Outline', scope: 'both', defaults: { color: '#000000', thickness: 2, opacity: 1 }, controls: ['color', 'thickness', 'opacity'] },
  { id: 'lift', label: 'Lift', scope: 'both', defaults: { distance: 6, softness: 14, opacity: 0.25 }, controls: ['distance', 'softness', 'opacity'] },
  { id: 'hollow', label: 'Hollow', scope: 'both', defaults: { color: '#000000', thickness: 2 }, controls: ['color', 'thickness'] },
  { id: 'emboss', label: 'Emboss', scope: 'both', defaults: { depth: 1, opacity: 0.5 }, controls: ['depth', 'opacity'] },
  { id: '3d', label: '3D', scope: 'both', defaults: { color: '#888888', depth: 6, angle: 45 }, controls: ['color', 'depth', 'angle'] },
  { id: 'splice', label: 'Splice', scope: 'both', defaults: { color: '#000000', distance: 6, angle: 45 }, controls: ['color', 'distance', 'angle'] },
  { id: 'echo', label: 'Echo', scope: 'both', defaults: { color: '#000000', distance: 8, angle: 45, opacity: 0.4 }, controls: ['color', 'distance', 'angle', 'opacity'] },
  { id: 'pixelate', label: 'Pixelate', scope: 'visual', defaults: { amount: 3 }, controls: ['amount'] },
  { id: 'glitch', label: 'Glitch', scope: 'both', defaults: { distance: 4, opacity: 0.8 }, controls: ['distance', 'opacity'] },
  { id: 'sketch', label: 'Sketch', scope: 'visual', defaults: { intensity: 1 }, controls: ['intensity'] },
  { id: 'poster', label: 'Poster', scope: 'visual', defaults: { levels: 2 }, controls: ['levels'] },
  { id: 'bloom', label: 'Bloom', scope: 'both', defaults: { color: '#ffffff', blur: 20, opacity: 0.7 }, controls: ['color', 'blur', 'opacity'] },
];

export const FILTER_CATALOG = [
  { id: 'none', label: 'None' },
  { id: 'grayscale', label: 'Grayscale' },
  { id: 'sepia', label: 'Sepia' },
  { id: 'bw', label: 'Black & White' },
  { id: 'vintage', label: 'Vintage' },
  { id: 'hdr', label: 'HDR' },
  { id: 'cinematic', label: 'Cinematic' },
  { id: 'warm', label: 'Warm' },
  { id: 'cool', label: 'Cool' },
  { id: 'bright', label: 'Bright' },
  { id: 'contrast', label: 'Contrast' },
  { id: 'saturation', label: 'Saturation' },
  { id: 'vivid', label: 'Vivid' },
  { id: 'dramatic', label: 'Dramatic' },
  { id: 'fade', label: 'Fade' },
  { id: 'blur', label: 'Blur' },
  { id: 'sharpen', label: 'Sharpen' },
  { id: 'duotone', label: 'Duotone', defaults: { highlight: '#f59e0b', shadow: '#7c3aed' }, controls: ['highlight', 'shadow'] },
  { id: 'retro', label: 'Retro' },
  { id: 'film', label: 'Film' },
  { id: 'mono', label: 'Mono' },
  { id: 'noir', label: 'Noir' },
  { id: 'golden-hour', label: 'Golden Hour' },
  { id: 'sunset', label: 'Sunset' },
  { id: 'moonlight', label: 'Moonlight' },
  { id: 'orange-teal', label: 'Orange & Teal' },
  { id: 'cyberpunk', label: 'Cyberpunk' },
  { id: 'pastel', label: 'Pastel' },
  { id: 'dreamy', label: 'Dreamy' },
  { id: 'luxury-gold', label: 'Luxury Gold' },
  { id: 'real-estate-bright', label: 'Real Estate Bright' },
  { id: 'product-clean', label: 'Product Clean' },
].map((f) => ({ scope: 'visual', defaults: { intensity: 1, ...(f.defaults || {}) }, controls: f.controls || ['intensity'], ...f }));

export const SHADOW_CATALOG = [
  { id: 'none', label: 'None', scope: 'both' },
  { id: 'drop', label: 'Drop Shadow', scope: 'both', defaults: { color: '#000000', blur: 8, distance: 8, angle: 90, opacity: 0.4 } },
  { id: 'soft', label: 'Soft Shadow', scope: 'both', defaults: { color: '#000000', blur: 20, distance: 6, angle: 90, opacity: 0.3 } },
  { id: 'hard', label: 'Hard Shadow', scope: 'both', defaults: { color: '#000000', blur: 0, distance: 6, angle: 90, opacity: 0.5 } },
  { id: 'glow', label: 'Glow Shadow', scope: 'both', defaults: { color: '#22d3ee', blur: 18, distance: 0, angle: 90, opacity: 0.7 } },
  { id: 'long', label: 'Long Shadow', scope: 'both', defaults: { color: '#000000', distance: 24, angle: 45, opacity: 0.3 } },
  { id: 'inner', label: 'Inner Shadow', scope: 'both', defaults: { color: '#000000', blur: 10, distance: 4, angle: 90, opacity: 0.5 } },
  { id: 'floating', label: 'Floating', scope: 'both', defaults: { color: '#000000', blur: 24, distance: 16, angle: 90, opacity: 0.25 } },
  { id: 'perspective', label: 'Perspective', scope: 'both', defaults: { color: '#000000', blur: 8, distance: 18, angle: 70, opacity: 0.3 } },
].map((s) => ({ controls: s.id === 'none' ? [] : ['color', 'blur', 'distance', 'angle', 'opacity'], ...s }));

const findDefaults = (catalog, id) => ({ ...(catalog.find((x) => x.id === id)?.defaults || {}) });
export const getEffectDefaults = (id) => findDefaults(EFFECT_CATALOG, id);
export const getFilterDefaults = (id) => findDefaults(FILTER_CATALOG, id);
export const getShadowDefaults = (id) => findDefaults(SHADOW_CATALOG, id);

const FILTER_IDS = new Set(FILTER_CATALOG.map((f) => f.id));

// ── Migration: normalize any saved shape into { effect, filterPreset, shadow } ─
export function getEffectFields(element) {
  if (!element) return { effect: null, filterPreset: null, shadow: null };
  let effect = element.effect || null;
  let filterPreset = element.filterPreset || null;
  let shadow = element.shadow || null;

  // Legacy unified-effect that was really a filter (hdr/vintage/cinematic/duotone).
  if (effect && FILTER_IDS.has(effect.type) && effect.type !== 'none') {
    if (!filterPreset) filterPreset = effect;
    effect = null;
  }
  // Legacy unified-effect that was really a shadow.
  if (effect && effect.type === 'shadow') {
    if (!shadow) shadow = { type: 'drop', params: effect.params || {} };
    effect = null;
  }
  // Legacy preset field.
  if (!shadow && element.shadowPreset && element.shadowPreset.type && element.shadowPreset.type !== 'none') {
    shadow = { type: 'drop', params: {} };
  }
  return { effect, filterPreset, shadow };
}

// ── EFFECT → CSS filter ───────────────────────────────────────────────────
function shadowChain(color, opacity, dist, angle, blur, n = 1, fade = false) {
  const rad = (num(angle, 90) * Math.PI) / 180;
  return Array.from({ length: n }, (_, i) => {
    const k = (i + 1) / n;
    const dx = num(dist) * Math.cos(rad) * (fade ? k : 1);
    const dy = num(dist) * Math.sin(rad) * (fade ? k : 1);
    const op = fade ? num(opacity, 0.4) * (1 - i / n) : num(opacity, 0.4);
    return `drop-shadow(${px(dx)} ${px(dy)} ${px(blur)} ${hexToRgba(color, op)})`;
  }).join(' ');
}

export function effectCss(element) {
  const { effect } = getEffectFields(element);
  if (!effect || !effect.type || effect.type === 'none') return '';
  const p = { ...getEffectDefaults(effect.type), ...(effect.params || {}) };
  switch (effect.type) {
    case 'glow': {
      const c = hexToRgba(p.color, num(p.opacity, 0.9));
      const passes = Math.max(1, Math.round(num(p.size, 1) * 2));
      return Array.from({ length: passes }, () => `drop-shadow(0 0 ${px(p.blur)} ${c})`).join(' ');
    }
    case 'neon': {
      const c = hexToRgba(p.color, num(p.opacity, 1));
      return `drop-shadow(0 0 ${px(num(p.blur, 16) / 2)} ${c}) drop-shadow(0 0 ${px(p.blur)} ${c}) brightness(${1 + 0.1 * num(p.intensity, 1.4)})`;
    }
    case 'outline': {
      // 8-direction hairline drop-shadows approximate an outline on alpha edges.
      const t = num(p.thickness, 2);
      const c = hexToRgba(p.color, num(p.opacity, 1));
      const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]];
      return dirs.map(([x, y]) => `drop-shadow(${px(x * t)} ${px(y * t)} 0 ${c})`).join(' ');
    }
    case 'lift':
      return `drop-shadow(0 ${px(p.distance ?? 6)} ${px(p.softness ?? 14)} ${hexToRgba('#000000', num(p.opacity, 0.25))})`;
    case 'emboss': {
      const d = num(p.depth, 1);
      const o = num(p.opacity, 0.5);
      return `drop-shadow(${px(d)} ${px(d)} 0 ${hexToRgba('#ffffff', o)}) drop-shadow(${px(-d)} ${px(-d)} 0 ${hexToRgba('#000000', o)})`;
    }
    case '3d':
      return shadowChain(p.color, 0.6, num(p.depth, 6) / Math.max(1, num(p.depth, 6)), p.angle, 0, num(p.depth, 6), false);
    case 'splice': {
      const rad = (num(p.angle, 45) * Math.PI) / 180;
      const dx = num(p.distance, 6) * Math.cos(rad);
      const dy = num(p.distance, 6) * Math.sin(rad);
      return `drop-shadow(${px(dx)} ${px(dy)} 0 ${hexToRgba(p.color, 0.8)}) drop-shadow(${px(-dx)} ${px(-dy)} 0 ${hexToRgba(p.color, 0.4)})`;
    }
    case 'echo':
      return shadowChain(p.color, num(p.opacity, 0.4), p.distance, p.angle, 0, 3, true);
    case 'glitch': {
      const d = num(p.distance, 4);
      const o = num(p.opacity, 0.8);
      return `drop-shadow(${px(-d)} 0 0 ${hexToRgba('#ff0040', o)}) drop-shadow(${px(d)} 0 0 ${hexToRgba('#00e5ff', o)})`;
    }
    case 'bloom': {
      const c = hexToRgba(p.color, num(p.opacity, 0.7));
      return `brightness(1.1) drop-shadow(0 0 ${px(p.blur)} ${c}) drop-shadow(0 0 ${px(num(p.blur, 20) * 2)} ${c})`;
    }
    case 'pixelate':
      return `contrast(${1 + num(p.amount, 3) * 0.05})`; // CSS approximation
    case 'sketch':
      return `grayscale(1) contrast(${1 + num(p.intensity, 1) * 0.6}) brightness(1.1)`;
    case 'poster':
      return `contrast(${1 + (4 - Math.min(4, num(p.levels, 2))) * 0.2}) saturate(1.4)`;
    default:
      return '';
  }
}

// ── FILTER → CSS filter ──────────────────────────────────────────────────────
export function filterPresetCss(element) {
  const { filterPreset } = getEffectFields(element);
  if (!filterPreset || !filterPreset.type || filterPreset.type === 'none') return '';
  const p = { ...getFilterDefaults(filterPreset.type), ...(filterPreset.params || {}) };
  const k = num(p.intensity, 1); // 0..1+ strength
  const mix = (base) => base; // (intensity already folded per-case)
  switch (filterPreset.type) {
    case 'grayscale': return `grayscale(${k})`;
    case 'sepia': return `sepia(${0.6 * k})`;
    case 'bw': return `grayscale(1) contrast(${1 + 0.3 * k})`;
    case 'vintage': return `sepia(${0.5 * k}) saturate(${1 + 0.2 * k}) brightness(0.95)`;
    case 'hdr': return `contrast(${1 + 0.4 * k}) saturate(${1 + 0.3 * k}) brightness(1.05)`;
    case 'cinematic': return `contrast(${1 + 0.2 * k}) saturate(${1 - 0.15 * k}) sepia(${0.15 * k})`;
    case 'warm': return `sepia(${0.3 * k}) saturate(${1 + 0.2 * k}) brightness(1.03)`;
    case 'cool': return `hue-rotate(${20 * k}deg) saturate(${1 + 0.15 * k})`;
    case 'bright': return `brightness(${1 + 0.25 * k})`;
    case 'contrast': return `contrast(${1 + 0.4 * k})`;
    case 'saturation': return `saturate(${1 + 0.6 * k})`;
    case 'vivid': return `saturate(${1 + 0.7 * k}) contrast(${1 + 0.1 * k})`;
    case 'dramatic': return `contrast(${1 + 0.5 * k}) brightness(0.95) saturate(${1 + 0.2 * k})`;
    case 'fade': return `brightness(1.1) saturate(${1 - 0.4 * k}) sepia(${0.1 * k})`;
    case 'blur': return `blur(${px(2 * k)})`;
    case 'sharpen': return `contrast(${1 + 0.5 * k}) brightness(1.02)`;
    case 'retro': return `sepia(${0.4 * k}) hue-rotate(${-15 * k}deg) saturate(${1 + 0.3 * k})`;
    case 'film': return `contrast(${1 + 0.25 * k}) sepia(${0.2 * k}) brightness(0.98)`;
    case 'mono': return `grayscale(1) brightness(${1 + 0.05 * k})`;
    case 'noir': return `grayscale(1) contrast(${1 + 0.5 * k}) brightness(0.92)`;
    case 'golden-hour': return `sepia(${0.3 * k}) saturate(${1 + 0.4 * k}) brightness(1.05) hue-rotate(${-10 * k}deg)`;
    case 'sunset': return `sepia(${0.25 * k}) saturate(${1 + 0.5 * k}) hue-rotate(${-15 * k}deg) brightness(1.02)`;
    case 'moonlight': return `brightness(${1 - 0.12 * k}) saturate(${1 - 0.3 * k}) contrast(${1 + 0.12 * k})`;
    case 'orange-teal': return `contrast(${1 + 0.15 * k}) saturate(${1 + 0.3 * k}) hue-rotate(${-10 * k}deg)`;
    case 'cyberpunk': return `contrast(${1 + 0.3 * k}) saturate(${1 + 0.6 * k}) hue-rotate(${-20 * k}deg) brightness(1.05)`;
    case 'pastel': return `saturate(${1 - 0.2 * k}) brightness(${1 + 0.1 * k}) contrast(${1 - 0.05 * k})`;
    case 'dreamy': return `brightness(${1 + 0.1 * k}) saturate(${1 + 0.1 * k}) contrast(${1 - 0.1 * k}) blur(${px(0.3 * k)})`;
    case 'luxury-gold': return `sepia(${0.4 * k}) saturate(${1 + 0.3 * k}) brightness(1.05) contrast(1.05)`;
    case 'real-estate-bright': return `brightness(${1 + 0.15 * k}) contrast(${1 + 0.1 * k}) saturate(${1 + 0.1 * k})`;
    case 'product-clean': return `brightness(${1 + 0.05 * k}) contrast(${1 + 0.05 * k}) saturate(${1 + 0.05 * k})`;
    case 'duotone': return element.id ? `url(#ef-duotone-${element.id})` : '';
    default: return mix('');
  }
}

// ── SHADOW → CSS (drop-shadow chain) or box-shadow (inner) ───────────────────
export function shadowFilterCss(element) {
  const { shadow } = getEffectFields(element);
  if (!shadow || !shadow.type || shadow.type === 'none' || shadow.type === 'inner') return '';
  const p = { ...getShadowDefaults(shadow.type), ...(shadow.params || {}) };
  const rad = (num(p.angle, 90) * Math.PI) / 180;
  const dx = num(p.distance) * Math.cos(rad);
  const dy = num(p.distance) * Math.sin(rad);
  if (shadow.type === 'long') {
    const steps = 16;
    return Array.from({ length: steps }, (_, i) =>
      `drop-shadow(${px(dx * (i + 1) / steps)} ${px(dy * (i + 1) / steps)} 0 ${hexToRgba(p.color, num(p.opacity, 0.3))})`).join(' ');
  }
  return `drop-shadow(${px(dx)} ${px(dy)} ${px(p.blur)} ${hexToRgba(p.color, num(p.opacity, 0.4))})`;
}

// Inner shadow can't be a drop-shadow filter; emit an inset box-shadow instead.
export function shadowBoxCss(element) {
  const { shadow } = getEffectFields(element);
  if (!shadow || shadow.type !== 'inner') return undefined;
  const p = { ...getShadowDefaults('inner'), ...(shadow.params || {}) };
  const rad = (num(p.angle, 90) * Math.PI) / 180;
  const dx = num(p.distance) * Math.cos(rad);
  const dy = num(p.distance) * Math.sin(rad);
  return `inset ${px(dx)} ${px(dy)} ${px(p.blur)} ${hexToRgba(p.color, num(p.opacity, 0.5))}`;
}

// ── Compose ──────────────────────────────────────────────────────────────────
export function composeFilter(element) {
  const parts = [];
  const base = element?.filter && element.filter !== 'none' ? element.filter : '';
  if (base) parts.push(base);
  const e = effectCss(element); if (e) parts.push(e);
  const f = filterPresetCss(element); if (f) parts.push(f);
  const s = shadowFilterCss(element); if (s) parts.push(s);
  return parts.length ? parts.join(' ') : 'none';
}

// Duotone is now a Filter; the SVG def is keyed off the (migrated) filterPreset.
export function duotoneTableValues(element) {
  const { filterPreset } = getEffectFields(element);
  if (!filterPreset || filterPreset.type !== 'duotone') return null;
  const p = { ...getFilterDefaults('duotone'), ...(filterPreset.params || {}) };
  const toRgb = (hex) => {
    const m = String(hex || '').match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    return m ? [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255] : [0, 0, 0];
  };
  const sh = toRgb(p.shadow);
  const hi = toRgb(p.highlight);
  return { id: `ef-duotone-${element.id}`, r: `${sh[0]} ${hi[0]}`, g: `${sh[1]} ${hi[1]}`, b: `${sh[2]} ${hi[2]}` };
}

export function hasDuotone(element) {
  return !!duotoneTableValues(element);
}

// Text-applicable effects → style patch (outline/hollow/neon/lift/emboss/echo +
// shadow). Used by the text render only.
export function effectTextStyle(element) {
  const { effect, shadow } = getEffectFields(element);
  const out = {};
  if (effect && effect.type && effect.type !== 'none') {
    const p = { ...getEffectDefaults(effect.type), ...(effect.params || {}) };
    if (effect.type === 'outline') out.WebkitTextStroke = `${px(p.thickness ?? 2)} ${hexToRgba(p.color, num(p.opacity, 1))}`;
    else if (effect.type === 'hollow') { out.WebkitTextStroke = `${px(p.thickness ?? 2)} ${p.color || '#000000'}`; out.color = 'transparent'; }
    else if (effect.type === 'neon') { const c = p.color || '#22d3ee'; const b = num(p.blur, 8); out.color = c; out.textShadow = `0 0 ${px(b / 2)} ${c}, 0 0 ${px(b)} ${c}, 0 0 ${px(b * 2)} ${c}`; }
    else if (effect.type === 'lift') out.textShadow = `0 ${px(p.distance ?? 6)} ${px(p.softness ?? 14)} ${hexToRgba('#000000', num(p.opacity, 0.25))}`;
  }
  // Text drop shadow via textShadow when the Shadow section is set (and not inner).
  if (shadow && shadow.type && shadow.type !== 'none' && shadow.type !== 'inner' && !out.textShadow) {
    const p = { ...getShadowDefaults(shadow.type), ...(shadow.params || {}) };
    const rad = (num(p.angle, 90) * Math.PI) / 180;
    out.textShadow = `${px(num(p.distance) * Math.cos(rad))} ${px(num(p.distance) * Math.sin(rad))} ${px(p.blur)} ${hexToRgba(p.color, num(p.opacity, 0.4))}`;
  }
  return out;
}
